import { normalizeContentRegion } from '../feed/region.ts';
import { normalizeUiLocale, type UiLocaleId } from '../i18n/locale.ts';
import { clampPlaybackRate } from '../playback/select.ts';
import {
  DEFAULT_DEARROW_SETTINGS,
  normalizeDeArrowSettings,
  type DeArrowSettings,
} from '../dearrow/model.ts';
import {
  DEFAULT_SPONSORBLOCK_SETTINGS,
  normalizeSponsorBlockSettings,
  type SponsorBlockSettings,
} from '../sponsorblock/model.ts';
import {
  DEFAULT_RETURN_YOUTUBE_DISLIKE_SETTINGS,
  normalizeReturnYoutubeDislikeSettings,
  type ReturnYoutubeDislikeSettings,
} from '../returnyoutubedislike/model.ts';
import {
  DEFAULT_YOUTUBE_EMBED_SETTINGS,
  normalizeYoutubeEmbedSettings,
  type YoutubeEmbedSettings,
} from '../youtubeembed/model.ts';
import {
  DEFAULT_PEERTUBE_SETTINGS,
  normalizePeerTubeSettings,
  type PeerTubeSettings,
} from '../peertube/model.ts';
import {
  DEFAULT_PLAYBACK_PREFERENCES,
  type AudioLanguagePref,
  type CodecFamily,
  type PlaybackPreferences,
  type SubtitlePref,
  type VideoQualityPref,
} from '../model/playback.ts';

/**
 * §8's Settings page, as the one document every platform stores.
 *
 * It is in core for R13's reason and one more: **this object is the sync payload**. §8 syncs the
 * overlapping subset to `/api/v1/auth/preferences`, and a settings model written in React would have
 * to be re-derived in Compose before a TV could push the same values to the same account.
 *
 * Three properties are structural rather than remembered:
 *
 * - **`playback` is the player's own `PlaybackPreferences`**, not a parallel set of fields. Quality,
 *   audio language, subtitle language and rate are exactly what `PlayerPort.apply()` takes, so
 *   "playback defaults" means *the same object the player already applies*, read from storage instead
 *   of from `DEFAULT_PLAYBACK_PREFERENCES`. Anything else would need a translation layer whose only job
 *   is to be forgotten about.
 * - **Every value has a normaliser and {@link normalizeSettings} is total** (R4). A settings document is
 *   read from `localStorage`, which means it can be a previous build's shape, hand-edited, or half
 *   written — and it is read on the boot path of every screen that plays a video.
 * - **The instance is a sync *target*, not the source of truth** (§8, and the API's own note). So this
 *   type is what Visious means, and `settings/sync.ts` maps it onto Invidious's 35 fields in both
 *   directions — losing what does not map rather than inventing what does not exist.
 *
 * What is deliberately **not** here: the credential (R9 — a token is not a setting), the instance URL
 * (it keys the token store, so it is platform state and switching it is a boot-level act), and
 * anything the API stores that Visious cannot honour — see `PREFERENCE_FIELDS_WITHOUT_A_SETTING`.
 */

/** §9's theme, as data. The `system` arm is why this is three values and not a boolean. */
export type ThemeChoice = 'system' | 'dark' | 'light';

export function normalizeThemeChoice(input: unknown): ThemeChoice {
  // The fallback is `dark`, §9's default — not `system`, which on a light desktop is light.
  return input === 'dark' || input === 'light' || input === 'system' ? input : 'dark';
}

/**
 * Where the app opens — §8's `default_home` row, restricted to destinations Visious actually has.
 *
 * The ids are `NavDestinationId`'s, deliberately: the landing page is one of the nav's own
 * destinations or it is a page the reader cannot get back to from the nav. It is not imported from
 * `links/nav.ts` because the dependency would run the wrong way — the nav is a *list* of these.
 */
export type LandingId = 'popular' | 'subscriptions' | 'playlists' | 'history';

const LANDING_IDS: readonly LandingId[] = ['popular', 'subscriptions', 'playlists', 'history'];

export function normalizeLanding(input: unknown): LandingId {
  return typeof input === 'string' && (LANDING_IDS as readonly string[]).includes(input)
    ? (input as LandingId)
    : // The default, and also where two retired values land: a previous build's `'home'` (`/` is not a
      // destination any more — it *is* the landing redirect) and `'trending'` (the screen is gone, and
      // the popular page is where its rows moved to). Both used to show what Popular shows.
      'popular';
}

/**
 * Which nav destinations the reader has hidden — display-only and deliberately local: the
 * pages stay reachable by URL, the landing setting still offers all five, and Invidious has no field
 * for four of them (`feed_menu` gates its own home tabs, not a menu, so it is not read or written).
 */
export function normalizeHiddenMenu(input: unknown): readonly LandingId[] {
  if (!Array.isArray(input)) return [];
  const hidden = LANDING_IDS.filter((id) => (input as readonly unknown[]).includes(id));
  return hidden.length === 0 ? [] : hidden;
}

export interface VisiousSettings {
  readonly theme: ThemeChoice;
  /**
   * The language the interface is written in — §8's `locale` row.
   *
   * `null` is **follow the system**, and it is the default rather than `'en'` for the same reason
   * `contentRegion` defaults to `null`: a stored language is a claim the reader made, and writing one
   * on their behalf makes the day a second catalogue ships a day when nothing changes for anybody.
   *
   * Typed as {@link UiLocaleId}, so it can only ever hold a language this build actually has copy for.
   * That is what makes the *other* direction safe — an account set to a language Visious cannot render
   * pulls as `null`-shaped "leave it alone" rather than as a setting that silently does nothing
   * (`i18n/locale.ts`, `settings/sync.ts`).
   */
  readonly uiLocale: UiLocaleId | null;
  /**
   * `null` is "no preference", and it must stay expressible: an empty `region=` answers **500** with a
   * leaked `KeyError`, so "no region" omits the parameter rather than sending it blank (`feed/region.ts`).
   */
  readonly contentRegion: string | null;
  readonly landing: LandingId;
  /** Nav destinations hidden from the menu — see {@link normalizeHiddenMenu}. */
  readonly hiddenMenu: readonly LandingId[];
  /** The player's own preferences — quality, audio language, subtitle language, rate. */
  readonly playback: PlaybackPreferences;
  /** 0–1, and separate from `muted` for §7's reason: unmuting restores the level, not the silence. */
  readonly volume: number;
  readonly muted: boolean;
  /**
   * Play YouTube's DRC ("Stable Volume") rendition of the audio, where a video has one.
   *
   * A *mode*, not a menu filter (revised 2026-08-01 — it used to show the DRC set as extra rows,
   * which doubled every language): the audio menu lists each language once and this decides which
   * rendition that row is, falling back to the one that exists. Off by default because DRC is a
   * processed version of the original rather than the thing itself. The player's audio menu carries
   * the same switch, so flipping it there writes this setting.
   */
  readonly useStableVolume: boolean;
  /**
   * Try to play on arrival. Off by default because the browser decides: Chrome's autoplay policy
   * blocks a sound-on `play()` without a prior user gesture on the origin, so this is a *request*
   * rather than a setting that always takes — `PlayerPort.play()`'s muted retry is what makes the
   * refusal visible instead of silent.
   */
  readonly autoplay: boolean;
  /**
   * Mirror watched state to the instance — §13.4, and the switch 3d shipped without.
   *
   * It maps onto Invidious's own `watch_history` preference (3e), which is the field its web UI uses
   * for the same decision, so turning it off here turns it off for the account rather than only for
   * this browser.
   */
  readonly mirrorWatchHistory: boolean;
  /**
   * Remember where you stopped, and show it — §8's `save_player_pos` row.
   *
   * **One switch for two visible things**, because both are the same stored fact: opening a video
   * continues from the playhead, and a thumbnail carries a bar showing how far in it is
   * (`playback/progress.ts`). Splitting them would offer a reader "draw bars but forget the
   * positions", which is not a state that can exist.
   *
   * The switch syncs and the positions cannot: the account holds `save_player_pos` and has nowhere to
   * put a timestamp, so this is the one setting whose *data* is local by force rather than by choice.
   * Off means nothing is recorded, nothing is resumed and no bar is drawn — the already-stored
   * positions are left alone rather than destroyed, and the settings page offers a count and a clear.
   */
  readonly resumePlayback: boolean;
  /**
   * Hide videos the reader has (nearly) finished from the subscriptions feed — the seen filter.
   * "Seen" reads off 4b's local playheads: finished, or at least 90 % in. Local-only by
   * construction, like the positions themselves — the account has nowhere to keep either (§8c) —
   * so this is not in the sync mapping.
   */
  readonly hideWatchedInFeed: boolean;
  /**
   * Storyboard previews on grid thumbnails (2026-08-02): hover a card for a moment and the
   * thumbnail becomes a flipbook of the video's storyboard tiles, the bottom bar following along.
   * On by default — the tiles come from the instance like every other image, so there is nothing
   * to opt into privacy-wise; the switch exists for readers who find moving cards noisy. Local-only
   * like `hideWatchedInFeed`: Invidious has no field for it.
   */
  readonly gridPreviews: boolean;
  /**
   * Keep a playing video as a corner player when the reader leaves `/watch` — the minimized
   * player, which docs/specs/player.md describes.
   *
   * **On by default**, because off is the behaviour this app had before the feature existed: the
   * player is torn down on navigation and the stored resume position makes that lossless. So the
   * switch costs nothing to turn off and the default is the feature working. Local-only like
   * `gridPreviews` — Invidious has no field for it — and read where the session is *retained* rather
   * than where the box is drawn, so turning it off closes a corner player that is already up.
   */
  readonly miniPlayer: boolean;
  /**
   * Trending rows on the popular page: the heading, its sentence, its own grid search and its
   * grid, above everything the popular page already shows.
   *
   * The setting *is* the feature's only entry point since the Trending screen was retired — there is no
   * nav item and no URL of its own any more (`/feed/trending` parses as the popular feed) — so it is
   * also what decides whether `/api/v1/trending` is requested at all. **Off by default**: the page it
   * appears on is most readers' landing page, and doubling what arrives there is not a default anyone
   * chose. Local-only like `hiddenMenu`: Invidious has no field for it, and its own `feed_menu` gates
   * that instance's home tabs rather than a page of ours.
   */
  readonly trendingOnPopular: boolean;
  /**
   * The YouTube-embed escape hatch (`youtubeembed/model.ts`) — the Playback tab's one-switch
   * gate on the "Watch on" menu's YouTube swap row (`player.md`). **Off by default**: it is a
   * deliberate escape hatch out of Visious's own player, not a default anyone asked for.
   * Local-only — Invidious has no field for it.
   */
  readonly youtubeEmbed: YoutubeEmbedSettings;
  /**
   * The SponsorBlock integration (`sponsorblock/model.ts`) — §8's Integrations tab. Local-only by
   * nature: Invidious has no field for any of it, so none of this is in the sync mapping, exactly
   * like `hiddenMenu`. Off by default, because enabling it sends (hashed) lookups to a service
   * that is not the instance.
   */
  readonly sponsorBlock: SponsorBlockSettings;
  /**
   * The DeArrow integration (`dearrow/model.ts`) — community titles and thumbnails on listing
   * cards (2026-08-02). Local-only for SponsorBlock's reason, and off by default for
   * SponsorBlock's reason too.
   */
  readonly deArrow: DeArrowSettings;
  /**
   * The Return YouTube Dislike integration (`returnyoutubedislike/model.ts`) — an estimated dislike
   * count right of the like count on the watch page. Local-only for SponsorBlock's reason, and off
   * by default for the same reason: enabling it sends the plain video id to a third-party service.
   */
  readonly returnYoutubeDislike: ReturnYoutubeDislikeSettings;
  /**
   * The PeerTube fallback (`peertube/model.ts`) — §8's Integrations tab, the idea
   * `integrations.md` named. Local-only for SponsorBlock's reason, and off by default because
   * enabling it can search a third-party instance for a matching video.
   */
  readonly peerTube: PeerTubeSettings;
  /**
   * The reader's appointed "Watch later" playlist (2026-08-02) — an ordinary playlist on the
   * account that the one-press buttons add to and remove from. `null` is "none appointed yet", and
   * pressing any Watch-later button then asks first. Local-only like `hiddenMenu`: the account has
   * no field for it, so on a second device the buttons ask once more — the *playlist* itself still
   * syncs, being the account's.
   */
  readonly watchLater: WatchLaterChoice | null;
}

/**
 * The id is the fact; the title is a display snapshot so the settings row can name the list without
 * a request, and it goes stale the honest way — renaming the playlist corrects it on the next pick.
 */
export interface WatchLaterChoice {
  readonly playlistId: string;
  readonly title: string;
}

export function normalizeWatchLaterChoice(input: unknown): WatchLaterChoice | null {
  if (!isRecord(input)) return null;
  const playlistId = input['playlistId'];
  if (typeof playlistId !== 'string' || playlistId.trim() === '') return null;
  return {
    playlistId: playlistId.trim(),
    title: typeof input['title'] === 'string' ? input['title'] : '',
  };
}

export const DEFAULT_SETTINGS: VisiousSettings = {
  // `dark`, not `system`: §9 promises a dark default, and following a light desktop out of
  // the box broke that promise for exactly the readers it was made to. `system` stays selectable.
  theme: 'dark',
  uiLocale: null,
  contentRegion: null,
  landing: 'popular',
  hiddenMenu: [],
  playback: DEFAULT_PLAYBACK_PREFERENCES,
  volume: 1,
  muted: false,
  useStableVolume: false,
  autoplay: false,
  // On by default, matching the instance's own `watch_history: true`. 3d already records watched
  // state, so defaulting this off would be this slice silently switching a shipped feature off.
  mirrorWatchHistory: true,
  /*
   * On by default, and here Visious deliberately differs from the instance — Invidious ships
   * `save_player_pos: false`, which is what the test account still holds. The reason is that this
   * setting's data never leaves the browser, so the conservative default buys no privacy that clearing
   * the store does not, while costing the feature its visibility: a progress bar that is off until
   * somebody finds a toggle is a progress bar nobody sees. The disagreement is not hidden — it shows up
   * as a row in the sync prompt's difference list the first time a reader signs in.
   */
  resumePlayback: true,
  // Off by default: hiding rows is a choice, and a feed that arrives pre-filtered looks broken.
  hideWatchedInFeed: false,
  gridPreviews: true,
  // On: off is what the app did before the corner player existed, so this default *is* the feature.
  miniPlayer: true,
  // Off: the popular page is where most readers land, and two grids is not a default anyone asked for.
  trendingOnPopular: false,
  youtubeEmbed: DEFAULT_YOUTUBE_EMBED_SETTINGS,
  sponsorBlock: DEFAULT_SPONSORBLOCK_SETTINGS,
  deArrow: DEFAULT_DEARROW_SETTINGS,
  returnYoutubeDislike: DEFAULT_RETURN_YOUTUBE_DISLIKE_SETTINGS,
  peerTube: DEFAULT_PEERTUBE_SETTINGS,
  watchLater: null,
};

/**
 * The deployment's own defaults for the settings the instance has no field for, read from the
 * public `config.json` — the same file §3 step 3 reads `instance` from, and the settings sit at its
 * root beside it rather than under a wrapper object.
 *
 * Only the **Visious-specific** settings are configurable here, deliberately: everything with an
 * Invidious field is the account's to decide (`settings/sync.ts` pulls it), so a config default for
 * `theme` or `region` would either be dead on arrival for a signed-in reader or fight the pull for
 * a signed-out one. A key for a synced setting is therefore ignored rather than honoured — as is
 * `watchLater`, whose value is a playlist id belonging to one account.
 *
 * The keys are named the way the settings page names them, because the file is edited by a deployer
 * rather than by this code:
 *
 * - `hiddenMenu` — nav destinations to hide, e.g. `["popular"]`
 * - `useStableVolume`, `hideWatchedInFeed`, `trendingOnPopular`, `gridPreviews`, `miniPlayer`,
 *   `forceAvc` — booleans
 * - `audioLanguage`, `subtitleLanguage` — a language tag, e.g. `"de"`; anything unreadable keeps
 *   the built-in default (original audio, subtitles off)
 * - `youtubeEmbed`, `sponsorBlock`, `deArrow`, `returnYoutubeDislike`, `peerTube` — the
 *   integrations, as the objects their own models define, through those models' normalisers. They
 *   are the one part of the file that is not flat: a category's colour or a service's address has
 *   no sensible flat spelling, and a second vocabulary for them would be a translation layer whose
 *   only job is to drift.
 *
 * Three of the playback keys are flat *aliases* instead, and that is the exception worth stating:
 * `PlaybackPreferences` mixes the codec and the two language preferences (no Invidious field) with
 * the quality height and the rate (both synced), so a `playback` object here would be half honoured
 * and half silently ignored.
 *
 * Total (R4): junk in the file — or no file at all — answers {@link DEFAULT_SETTINGS}.
 */
export function defaultSettingsFromConfig(configJson: unknown): VisiousSettings {
  if (!isRecord(configJson)) return DEFAULT_SETTINGS;

  const audio = normalizeLanguageTag(configJson['audioLanguage']);
  const subtitle = normalizeLanguageTag(configJson['subtitleLanguage']);
  const playback = DEFAULT_SETTINGS.playback;

  return {
    ...DEFAULT_SETTINGS,
    hiddenMenu: normalizeHiddenMenu(configJson['hiddenMenu']),
    useStableVolume: configBoolean(configJson['useStableVolume'], DEFAULT_SETTINGS.useStableVolume),
    hideWatchedInFeed: configBoolean(configJson['hideWatchedInFeed'], DEFAULT_SETTINGS.hideWatchedInFeed),
    trendingOnPopular: configBoolean(configJson['trendingOnPopular'], DEFAULT_SETTINGS.trendingOnPopular),
    gridPreviews: configBoolean(configJson['gridPreviews'], DEFAULT_SETTINGS.gridPreviews),
    miniPlayer: configBoolean(configJson['miniPlayer'], DEFAULT_SETTINGS.miniPlayer),
    youtubeEmbed: normalizeYoutubeEmbedSettings(configJson['youtubeEmbed']),
    sponsorBlock: normalizeSponsorBlockSettings(configJson['sponsorBlock']),
    deArrow: normalizeDeArrowSettings(configJson['deArrow']),
    returnYoutubeDislike: normalizeReturnYoutubeDislikeSettings(configJson['returnYoutubeDislike']),
    peerTube: normalizePeerTubeSettings(configJson['peerTube']),
    playback: {
      ...playback,
      ...(configBoolean(configJson['forceAvc'], false)
        ? { quality: { ...playback.quality, codec: 'avc' as const } }
        : {}),
      ...(audio === null ? {} : { audio: { kind: 'lang' as const, code: audio, allowDub: true } }),
      ...(subtitle === null
        ? {}
        : { subtitle: { kind: 'lang' as const, code: subtitle, allowAutoGenerated: true } }),
    },
  };
}

/**
 * A boolean from the file, or the built-in default for anything that is not one.
 *
 * Written against the default rather than as `=== true`, because two of these settings default
 * **on**: `gridPreviews` and `miniPlayer` read `false` from the file as a real choice, while a typo
 * has to leave the feature working rather than silently turning it off.
 */
function configBoolean(input: unknown, fallback: boolean): boolean {
  return typeof input === 'boolean' ? input : fallback;
}

// ---------------------------------------------------------------------------
// Normalisers — each one total, because every input is either storage or the API
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeCodec(input: unknown): CodecFamily | undefined {
  return input === 'avc' || input === 'av1' || input === 'vp9' || input === 'other'
    ? input
    : undefined;
}

/** A pinned height has to be a plausible one: a stored `0` or `1e9` would pin to nothing. */
function normalizeHeight(input: unknown): number | null {
  if (typeof input !== 'number' || !Number.isFinite(input)) return null;
  const height = Math.round(input);
  return height >= 144 && height <= 4320 ? height : null;
}

export function normalizeQualityPref(input: unknown): VideoQualityPref {
  if (!isRecord(input)) return DEFAULT_PLAYBACK_PREFERENCES.quality;
  const codec = normalizeCodec(input['codec']);
  if (input['kind'] === 'pinned') {
    const height = normalizeHeight(input['height']);
    // A pinned height that no longer reads as one falls back to `auto` rather than to a guess: the
    // ladder differs per video anyway, so "adaptive" is the honest reading of an unusable pin.
    if (height !== null) return codec === undefined ? { kind: 'pinned', height } : { kind: 'pinned', height, codec };
  }
  const maxHeight = normalizeHeight(input['maxHeight']);
  return {
    kind: 'auto',
    ...(maxHeight === null ? {} : { maxHeight }),
    ...(codec === undefined ? {} : { codec }),
  };
}

/**
 * A language tag as a *preference*, not as a validated BCP-47 value.
 *
 * Deliberately loose: the tag has to match a track the manifest turned out to have, and that
 * comparison is `langTagsEqual`/`langTagsRelated`'s job. What this refuses is a value that could not
 * be a tag at all — an empty string, something with a space or a slash in it, or 40 characters of
 * junk — because those cannot match anything and would sit in the settings page as a live-looking
 * choice that never applies.
 */
export function normalizeLanguageTag(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const tag = input.trim();
  return /^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(tag) ? tag : null;
}

export function normalizeAudioPref(input: unknown): AudioLanguagePref {
  if (!isRecord(input) || input['kind'] !== 'lang') return { kind: 'auto' };
  const code = normalizeLanguageTag(input['code']);
  if (code === null) return { kind: 'auto' };
  // No `drc` here on purpose: the persisted choice is `useStableVolume`, and a stored copy of it on
  // the preference would be a second, staler source of truth (`model/playback.ts`).
  return { kind: 'lang', code, allowDub: input['allowDub'] !== false };
}

export function normalizeSubtitlePref(input: unknown): SubtitlePref {
  if (!isRecord(input) || input['kind'] !== 'lang') return { kind: 'off' };
  const code = normalizeLanguageTag(input['code']);
  if (code === null) return { kind: 'off' };
  return { kind: 'lang', code, allowAutoGenerated: input['allowAutoGenerated'] !== false };
}

export function normalizePlaybackPreferences(input: unknown): PlaybackPreferences {
  if (!isRecord(input)) return DEFAULT_PLAYBACK_PREFERENCES;
  return {
    quality: normalizeQualityPref(input['quality']),
    audio: normalizeAudioPref(input['audio']),
    subtitle: normalizeSubtitlePref(input['subtitle']),
    // Through core's own snap, so a stale build's 1.15 becomes a rate the menu can show as selected.
    playbackRate:
      typeof input['playbackRate'] === 'number'
        ? clampPlaybackRate(input['playbackRate'])
        : DEFAULT_PLAYBACK_PREFERENCES.playbackRate,
  };
}

function normalizeVolume(input: unknown): number {
  return typeof input === 'number' && Number.isFinite(input)
    ? Math.min(1, Math.max(0, input))
    : DEFAULT_SETTINGS.volume;
}

/** A stored document → settings. Total: anything unreadable falls back per field, never wholesale. */
export function normalizeSettings(input: unknown): VisiousSettings {
  if (!isRecord(input)) return DEFAULT_SETTINGS;
  return {
    theme: normalizeThemeChoice(input['theme']),
    uiLocale: normalizeUiLocale(input['uiLocale']),
    contentRegion: normalizeContentRegion(
      typeof input['contentRegion'] === 'string' ? input['contentRegion'] : null,
    ),
    landing: normalizeLanding(input['landing']),
    hiddenMenu: normalizeHiddenMenu(input['hiddenMenu']),
    playback: normalizePlaybackPreferences(input['playback']),
    volume: normalizeVolume(input['volume']),
    muted: input['muted'] === true,
    useStableVolume: input['useStableVolume'] === true,
    autoplay: input['autoplay'] === true,
    mirrorWatchHistory: input['mirrorWatchHistory'] !== false,
    resumePlayback: input['resumePlayback'] !== false,
    hideWatchedInFeed: input['hideWatchedInFeed'] === true,
    gridPreviews: input['gridPreviews'] !== false,
    miniPlayer: input['miniPlayer'] !== false,
    trendingOnPopular: input['trendingOnPopular'] === true,
    youtubeEmbed: normalizeYoutubeEmbedSettings(input['youtubeEmbed']),
    sponsorBlock: normalizeSponsorBlockSettings(input['sponsorBlock']),
    deArrow: normalizeDeArrowSettings(input['deArrow']),
    returnYoutubeDislike: normalizeReturnYoutubeDislikeSettings(input['returnYoutubeDislike']),
    peerTube: normalizePeerTubeSettings(input['peerTube']),
    watchLater: normalizeWatchLaterChoice(input['watchLater']),
  };
}

// ---------------------------------------------------------------------------
// Keeping identity across a re-read
// ---------------------------------------------------------------------------

/**
 * The settings as they now are, with every part that did not move kept at its **previous identity**.
 *
 * The store re-reads the whole document whenever anything might have changed — a write here, a
 * `storage` event from another tab, the deployment's defaults arriving. Most of those re-reads
 * produce a document equal to the one already published: another tab stamping its sync timestamp
 * moves a field that is stored beside the settings and is not one of them, so the reader's settings
 * are byte-for-byte what they were. Handing that back as a *new object* told every subscriber the
 * settings had changed, and a grid asks that question once per card.
 *
 * So this answers two questions in one pass, and both are identity comparisons at the call site:
 *
 * - **Did anything change at all?** `next === previous` means no, and the store can return without
 *   notifying anyone.
 * - **Did this part change?** A subscriber reading one branch — `settings.deArrow` — sees the same
 *   object it saw last time unless that branch itself moved, so changing the theme does not re-render
 *   every card that only cares about DeArrow.
 *
 * The walk is over plain values rather than over the field list on purpose. A version keyed to the
 * fields would be one more place to forget a new setting, which this file has already paid for once
 * (see {@link StoredSettings} in the web store): every gate passes over a missing key.
 */
export function reuseUnchangedSettings(
  next: VisiousSettings,
  previous: VisiousSettings | null,
): VisiousSettings {
  if (previous === null) return next;
  return reuseUnchanged(next, previous) as VisiousSettings;
}

function isPlainRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `previous` where the two are equal, otherwise a value shaped like `next` whose equal branches are
 * `previous`'s objects. Total over anything a settings document can hold: primitives, arrays of
 * them, and records.
 */
function reuseUnchanged(next: unknown, previous: unknown): unknown {
  if (Object.is(next, previous)) return previous;

  if (Array.isArray(next)) {
    if (!Array.isArray(previous) || next.length !== previous.length) return next;
    const merged = next.map((item, index) => reuseUnchanged(item, previous[index]));
    return merged.every((item, index) => item === previous[index]) ? previous : merged;
  }

  if (isPlainRecord(next)) {
    if (!isPlainRecord(previous)) return next;
    const keys = Object.keys(next);
    // A shape difference is a change, and comparing key by key from here on is then sound.
    if (keys.length !== Object.keys(previous).length) return next;
    if (!keys.every((key) => key in previous)) return next;

    const merged: Record<string, unknown> = {};
    let unchanged = true;
    for (const key of keys) {
      const value = reuseUnchanged(next[key], previous[key]);
      merged[key] = value;
      if (value !== previous[key]) unchanged = false;
    }
    return unchanged ? previous : merged;
  }

  // Two different primitives.
  return next;
}
